import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, FormField, Input, RadioGroup, Toggle, useToast } from '@bbpos/ui';
import {
  getCurrencyDecimals,
  getSubunitMultiplier,
  majorToAmount,
  resolveCashRoundingRule,
  type CashRoundingRule,
} from '@bbpos/utils';
import { locationService } from '@/services/location.service';
import type { Location } from '@/types/location';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { CashRoundingFields, describeCashRounding } from '@/components/features/settings/CashRoundingFields';
import { LocationRegistersCard } from './LocationRegistersCard';
import { LocationTimezoneSelect } from './LocationTimezoneSelect';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ReceiptSettings {
  width: '58mm' | '80mm';
  auto_print: boolean;
  default_printer: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const RECEIPT_WIDTH_OPTIONS = [
  {
    value: '58mm',
    label: '58mm',
    description: 'Narrow thermal paper (compact printers)',
  },
  {
    value: '80mm',
    label: '80mm',
    description: 'Standard thermal paper (most commercial printers)',
  },
];

/**
 * System-wide defaults applied when a location has no receipt_format override.
 * These represent the tenant-wide baseline the location can override per FR-LOC-002.
 */
export const RECEIPT_DEFAULTS: ReceiptSettings = {
  width: '80mm',
  auto_print: false,
  default_printer: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseReceiptSettings(raw?: Record<string, unknown>): ReceiptSettings {
  if (!raw) return { ...RECEIPT_DEFAULTS };
  return {
    width: raw.width === '58mm' ? '58mm' : RECEIPT_DEFAULTS.width,
    auto_print:
      typeof raw.auto_print === 'boolean' ? raw.auto_print : RECEIPT_DEFAULTS.auto_print,
    default_printer:
      typeof raw.default_printer === 'string'
        ? raw.default_printer
        : RECEIPT_DEFAULTS.default_printer,
  };
}

/** Minor units → the plain major-unit text an input shows ('' for none). */
function minorToInput(minor: number | null | undefined, currency: string): string {
  if (minor == null) return '';
  return (minor / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency));
}

/** Input text → minor units; null when blank, undefined when not a valid amount. */
export function parseAllowedDifference(text: string, currency: string): number | null | undefined {
  const t = text.trim();
  if (!t) return null;
  const decimals = getCurrencyDecimals(currency);
  const pattern = decimals > 0 ? new RegExp(`^\\d+(\\.\\d{0,${decimals}})?$`) : /^\d+$/;
  if (!pattern.test(t)) return undefined;
  return majorToAmount(Number(t), currency);
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

// ─── Component ────────────────────────────────────────────────────────────────

interface LocationSettingsTabProps {
  location: Location;
}

export function LocationSettingsTab({ location }: LocationSettingsTabProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [settings, setSettings] = useState<ReceiptSettings>(() =>
    parseReceiptSettings(location.receipt_format),
  );

  // Cash rounding override (T07): off = this location follows the tenant rule.
  const currency = useTenantCurrency();
  const tenantRounding = useAuthStore((s) => s.profile?.tenant) as
    | { cash_rounding_step?: number | null; cash_rounding_mode?: string | null }
    | undefined;
  const tenantRule = resolveCashRoundingRule(currency, {
    step: tenantRounding?.cash_rounding_step, mode: tenantRounding?.cash_rounding_mode,
  });
  const [ownRounding, setOwnRounding] = useState(location.cash_rounding_step != null);
  const [rounding, setRounding] = useState<CashRoundingRule>(() =>
    location.cash_rounding_step != null
      ? resolveCashRoundingRule(currency, null, { step: location.cash_rounding_step, mode: location.cash_rounding_mode })
      : tenantRule,
  );

  // T12: allowed cash difference at register closing; above it a manager PIN.
  const [allowedDiff, setAllowedDiff] = useState(() => minorToInput(location.cash_difference_allowed, currency));
  const allowedDiffMinor = parseAllowedDifference(allowedDiff, currency);
  // T14: the store's own timezone; null = the business's.
  const [timezone, setTimezone] = useState<string | null>(location.timezone ?? null);
  const canEditRegisters = ['OWNER', 'SUPER_ADMIN', 'ADMIN'].includes(
    String(useAuthStore.getState().profile?.role ?? ''),
  );

  // Sync when location prop changes (e.g. after a save invalidation)
  useEffect(() => {
    setAllowedDiff(minorToInput(location.cash_difference_allowed, currency));
    setTimezone(location.timezone ?? null);
    setSettings(parseReceiptSettings(location.receipt_format));
    setOwnRounding(location.cash_rounding_step != null);
    if (location.cash_rounding_step != null) {
      setRounding(resolveCashRoundingRule(currency, null, {
        step: location.cash_rounding_step, mode: location.cash_rounding_mode,
      }));
    }
  }, [location, currency]);

  const saveMutation = useMutation({
    mutationFn: () =>
      locationService.update(location.id, {
        receipt_format: {
          width: settings.width,
          auto_print: settings.auto_print,
          default_printer: settings.default_printer || undefined,
        },
        cash_rounding_step: ownRounding ? rounding.step : null,
        cash_rounding_mode: ownRounding ? rounding.mode : null,
        cash_difference_allowed: allowedDiffMinor ?? null,
        timezone,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location', location.id] });
      toast({ title: 'Settings saved', variant: 'success' });
    },
    onError: (err: Error) => {
      toast({ title: err.message ?? 'Failed to save settings', variant: 'error' });
    },
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      saveMutation.mutate();
    },
    [saveMutation],
  );

  return (
    <>
    <form onSubmit={handleSubmit} className="animate-slide-up">
      <div className={`${GLASS_PANEL} p-6`}>
        {/* Section header */}
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-1">
          Print &amp; Receipt Settings
        </h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-6">
          Settings here apply to <strong>{location.name}</strong> only and override
          tenant-wide defaults.
        </p>

        <div className="space-y-6">
          {/* ── Receipt width ───────────────────────────────────── */}
          <div>
            <RadioGroup
              label="Receipt width"
              options={RECEIPT_WIDTH_OPTIONS}
              value={settings.width}
              onChange={(v) =>
                setSettings((s) => ({ ...s, width: v as '58mm' | '80mm' }))
              }
              variant="card"
            />
            <p className="mt-1.5 text-xs text-neutral-400 dark:text-neutral-500">
              Tenant default: {RECEIPT_DEFAULTS.width}
            </p>
          </div>

          {/* ── Auto-print toggle ───────────────────────────────── */}
          <div>
            <Toggle
              id="receipt-auto-print"
              label="Auto-print receipts"
              description="Automatically send a print job after each completed bill"
              checked={settings.auto_print}
              onChange={(v) => setSettings((s) => ({ ...s, auto_print: v }))}
            />
            <p className="mt-1.5 text-xs text-neutral-400 dark:text-neutral-500">
              Tenant default: {RECEIPT_DEFAULTS.auto_print ? 'On' : 'Off'}
            </p>
          </div>

          {/* ── Default printer ─────────────────────────────────── */}
          <FormField label="Default Printer" htmlFor="receipt-default-printer">
            <Input
              id="receipt-default-printer"
              placeholder="e.g., Epson TM-T82"
              value={settings.default_printer}
              onChange={(v) => setSettings((s) => ({ ...s, default_printer: v }))}
            />
            <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
              Printer name as shown in your OS. Leave blank to use the system default.
            </p>
          </FormField>

          {/* ── Cash rounding override (T07) ────────────────────── */}
          <div className="rounded-xl border border-teal-100 dark:border-teal-900/50 bg-teal-50/50 dark:bg-teal-900/10 p-4 space-y-3">
            <Toggle
              id="location-own-cash-rounding"
              label="Own cash rounding"
              description="Round cash at this location differently from the business setting"
              checked={ownRounding}
              onChange={setOwnRounding}
            />
            {ownRounding ? (
              <CashRoundingFields currency={currency} value={rounding} onChange={setRounding} example={5237} />
            ) : (
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Business setting: {describeCashRounding(tenantRule, currency)}
              </p>
            )}
          </div>

          {/* ── Store timezone (T14) ────────────────────────────── */}
          <LocationTimezoneSelect id="location-timezone" value={timezone} onChange={setTimezone} />

          {/* ── Allowed cash difference at register closing (T12) ─ */}
          <FormField label="Allowed cash difference" htmlFor="location-cash-difference-allowed">
            <Input
              id="location-cash-difference-allowed"
              inputMode="decimal"
              placeholder="0"
              value={allowedDiff}
              onChange={setAllowedDiff}
            />
            <p className={`mt-1 text-xs ${allowedDiffMinor === undefined ? 'text-danger-600' : 'text-neutral-400 dark:text-neutral-500'}`}>
              {allowedDiffMinor === undefined
                ? 'Enter an amount, like 50 or 50.00.'
                : 'Any difference when a register closes needs a reason. Above this amount a manager must also approve with their PIN. Blank = 0.'}
            </p>
          </FormField>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end mt-8 pt-4">
          <Button
            variant="primary"
            type="submit"
            loading={saveMutation.isPending}
            disabled={allowedDiffMinor === undefined}
          >
            Save Settings
          </Button>
        </div>
      </div>
    </form>
    <LocationRegistersCard locationId={location.id} canEdit={canEditRegisters} />
    </>
  );
}
